/**
 * StyleRenderingSection 風格渲染區塊
 *
 * 提供座位規劃圖的風格渲染功能：
 * - 預設風格按鈕
 * - 自訂風格關鍵字
 * - 自訂完整提示詞
 * - 渲染結果顯示與下載
 */

import { Section } from '@/components/layout/Section';
import { Spinner } from '@/components/shared/ProgressIndicator';
import { renderStyleImage, getStyleOptions } from '@/api/style-rendering-api';
import { downloadBlob } from '@/utils/file';
import { escapeHtml } from '@/utils/security';

// ========================================================================
// 型別定義
// ========================================================================

export interface StyleRenderingSectionOptions {
  /** 取得 Canvas 元素的回呼 */
  onGetCanvas?: () => HTMLCanvasElement | null;
}

// ========================================================================
// StyleRenderingSection 類別
// ========================================================================

export class StyleRenderingSection {
  private section: Section;
  private options: StyleRenderingSectionOptions;

  // UI 元素
  private customStyleInput: HTMLInputElement | null = null;
  private customPromptInput: HTMLTextAreaElement | null = null;
  private styleButtonsContainer: HTMLDivElement | null = null;
  private loadingContainer: HTMLDivElement | null = null;
  private resultContainer: HTMLDivElement | null = null;
  private resultImage: HTMLImageElement | null = null;
  private resultTitle: HTMLSpanElement | null = null;
  private spinner: Spinner | null = null;

  // 狀態
  private lastUsedStyle: string | null = null;
  private currentImageUrl: string | null = null;

  constructor(options: StyleRenderingSectionOptions = {}) {
    this.options = options;

    // 建立區段
    this.section = new Section({
      title: '步驟 4：風格渲染',
      description: '選擇風格，將座位規劃圖渲染成更具視覺效果的平面圖',
      stepNumber: 4,
      visible: false,
    });

    this.assembleUI();
  }

  // ========================================================================
  // 公開方法
  // ========================================================================

  mount(container: HTMLElement): void {
    this.section.mount(container);
  }

  show(): void {
    this.section.show();
  }

  hide(): void {
    this.section.hide();
  }

  /**
   * 取得當前渲染結果的 base64 字串（用於儲存）
   * @returns base64 字串（不含 data URL 前綴），如果沒有渲染結果則返回 null
   */
  async getRenderedImageBase64(): Promise<string | null> {
    if (!this.currentImageUrl || !this.resultImage?.src) {
      return null;
    }

    try {
      // 從 Blob URL 取得 Blob
      const response = await fetch(this.currentImageUrl);
      const blob = await response.blob();

      // 轉換為 base64
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // 移除 "data:image/png;base64," 前綴
          const base64 = result.split(',')[1];
          if (base64) {
            resolve(base64);
          } else {
            reject(new Error('無法提取 base64 字串'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('[風格渲染] 取得 base64 失敗:', error);
      return null;
    }
  }

  /**
   * 載入已儲存的渲染結果
   * @param base64 - base64 字串（不含 data URL 前綴）
   * @param styleName - 風格名稱（可選）
   */
  loadRenderedImage(base64: string, styleName?: string): void {
    const dataUrl = `data:image/png;base64,${base64}`;
    this.displayResult(dataUrl, styleName || null, null);
    this.show();
  }

  destroy(): void {
    // 釋放 Blob URL
    if (this.currentImageUrl) {
      URL.revokeObjectURL(this.currentImageUrl);
    }
    this.section.destroy();
  }

  // ========================================================================
  // 私有方法 - UI 建構
  // ========================================================================

  private assembleUI(): void {
    const content = this.section.getContent();

    content.innerHTML = `
      <div class="space-y-6">
        <!-- 自訂風格關鍵字 -->
        <div class="px-1">
          <label for="customStyleInput" class="text-lg font-semibold mb-2 text-center text-gray-700 block">
            自訂風格關鍵字
          </label>
          <div class="flex gap-2">
            <input
              type="text"
              id="customStyleInput"
              class="flex-grow p-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
              placeholder="例如：Cyberpunk, vibrant colors, neon lights"
            />
            <button
              id="customStyleGenerateBtn"
              class="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 transition-colors"
            >
              生成
            </button>
          </div>
        </div>

        <!-- 自訂完整提示詞 -->
        <div class="px-1">
          <label for="customPromptInput" class="text-lg font-semibold mb-2 text-center text-gray-700 block">
            自訂完整提示詞
          </label>
          <textarea
            id="customPromptInput"
            rows="4"
            class="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
            placeholder="輸入完整的 prompt，將直接用於 Gemini API..."
          ></textarea>
          <button
            id="customPromptGenerateBtn"
            class="px-4 py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 transition-colors mt-2 w-full"
          >
            使用自訂完整提示詞
          </button>
        </div>

        <!-- 風格按鈕 -->
        <div id="styleButtons" class="flex flex-wrap gap-2">
          <!-- Style buttons will be injected here -->
        </div>

        <!-- 載入中 -->
        <div id="loadingContainer" class="hidden text-center py-12">
          <div id="spinnerContainer"></div>
          <p class="mt-4 text-lg font-medium text-gray-700">AI 正在渲染您的平面圖...</p>
          <p class="text-sm text-gray-500 mt-1">請稍候，這可能需要一點時間。</p>
        </div>

        <!-- 渲染結果 -->
        <div id="resultContainer" class="hidden">
          <h3 class="text-lg font-semibold mb-3">
            渲染結果<span id="resultTitle" class="text-primary-600"></span>
          </h3>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <img id="resultImage" class="w-full h-auto rounded-lg shadow-md" alt="風格渲染平面圖" />
          </div>
          <div class="mt-4 text-center flex items-center justify-center gap-4 flex-wrap">
            <button
              id="regenerateBtn"
              class="px-4 py-2 bg-blue-100 text-blue-700 font-medium rounded-md hover:bg-blue-200 transition-colors"
            >
              重新生成
            </button>
            <button
              id="styleDownloadBtn"
              class="px-4 py-2 bg-green-100 text-green-700 font-medium rounded-md hover:bg-green-200 transition-colors"
            >
              下載 PNG
            </button>
          </div>
        </div>
      </div>
    `;

    // 初始化元素參考
    this.customStyleInput = content.querySelector('#customStyleInput');
    this.customPromptInput = content.querySelector('#customPromptInput');
    this.styleButtonsContainer = content.querySelector('#styleButtons');
    this.loadingContainer = content.querySelector('#loadingContainer');
    this.resultContainer = content.querySelector('#resultContainer');
    this.resultImage = content.querySelector('#resultImage');
    this.resultTitle = content.querySelector('#resultTitle');

    // 初始化 Spinner
    const spinnerContainer = content.querySelector('#spinnerContainer');
    if (spinnerContainer) {
      this.spinner = new Spinner();
      this.spinner.mount(spinnerContainer as HTMLElement);
    }

    // 繪製風格按鈕
    this.renderStyleButtons();

    // 綁定事件
    this.bindEvents(content);
  }

  private renderStyleButtons(): void {
    if (!this.styleButtonsContainer) return;

    const styles = getStyleOptions();
    const buttonsHTML = styles
      .map((style) => {
        // 🔒 安全性：轉義 data 屬性值和按鈕文字
        const safeStyle = escapeHtml(style);
        const safeDataStyle = style
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#x27;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');

        return `
          <button
            class="style-btn px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 hover:border-primary-500 hover:text-primary-600 transition-colors"
            data-style="${safeDataStyle}"
          >
            ${safeStyle}
          </button>
        `;
      })
      .join('');

    this.styleButtonsContainer.innerHTML = buttonsHTML;
  }

  private bindEvents(content: HTMLElement): void {
    // 自訂風格關鍵字生成
    const customStyleGenerateBtn = content.querySelector('#customStyleGenerateBtn');
    customStyleGenerateBtn?.addEventListener('click', () => {
      const customStyle = this.customStyleInput?.value.trim();
      if (customStyle) {
        this.handleStyleGeneration(customStyle);
      } else {
        alert('請輸入自訂風格關鍵字');
      }
    });

    // 自訂完整提示詞生成
    const customPromptGenerateBtn = content.querySelector('#customPromptGenerateBtn');
    customPromptGenerateBtn?.addEventListener('click', () => {
      const customPrompt = this.customPromptInput?.value.trim();
      if (customPrompt) {
        this.handleStyleGeneration(null, customPrompt);
      } else {
        alert('請輸入完整的提示詞');
      }
    });

    // 風格按鈕點擊
    this.styleButtonsContainer?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('.style-btn');
      if (btn) {
        const style = (btn as HTMLElement).dataset.style;
        if (style) {
          this.handleStyleGeneration(style);
        }
      }
    });

    // 重新生成按鈕
    const regenerateBtn = content.querySelector('#regenerateBtn');
    regenerateBtn?.addEventListener('click', () => {
      if (this.lastUsedStyle !== null) {
        this.handleStyleGeneration(this.lastUsedStyle);
      }
    });

    // 下載按鈕
    const downloadBtn = content.querySelector('#styleDownloadBtn');
    downloadBtn?.addEventListener('click', () => this.handleDownload());
  }

  // ========================================================================
  // 私有方法 - 風格渲染
  // ========================================================================

  private async handleStyleGeneration(style: string | null, customPrompt: string | null = null): Promise<void> {
    // 取得 Canvas
    const canvas = this.options.onGetCanvas?.();
    if (!canvas) {
      alert('請先繪製座位規劃圖');
      return;
    }

    this.lastUsedStyle = style;
    this.hideResult();
    this.showLoading();

    try {
      // 從 canvas 獲取 Blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('無法從 Canvas 轉換為 Blob'));
        }, 'image/png');
      });

      // 呼叫 API
      const response = await renderStyleImage({
        image: blob,
        style: style || undefined,
        customPrompt: customPrompt || undefined,
      });

      // 顯示結果
      this.displayResult(response.imageUrl, style, customPrompt);

    } catch (error) {
      console.error('[風格渲染] 渲染失敗:', error);
      alert(`渲染失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      this.hideLoading();
    }
  }

  private showLoading(): void {
    this.loadingContainer?.classList.remove('hidden');
  }

  private hideLoading(): void {
    this.loadingContainer?.classList.add('hidden');
  }

  private displayResult(imageUrl: string, style: string | null, customPrompt: string | null): void {
    // 釋放舊的 URL
    if (this.currentImageUrl) {
      URL.revokeObjectURL(this.currentImageUrl);
    }

    this.currentImageUrl = imageUrl;

    if (this.resultImage) {
      this.resultImage.src = imageUrl;
    }

    if (this.resultTitle) {
      this.resultTitle.textContent = customPrompt
        ? ' - 自訂提示詞'
        : style
          ? ` - ${style}`
          : '';
    }

    this.resultContainer?.classList.remove('hidden');
  }

  private hideResult(): void {
    this.resultContainer?.classList.add('hidden');
  }

  private handleDownload(): void {
    if (!this.resultImage?.src) {
      alert('請先完成風格渲染');
      return;
    }

    // 創建 canvas 來添加浮水印
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = this.resultImage;

    // 設定 canvas 尺寸與圖片相同
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // 繪製原始圖片
    ctx.drawImage(img, 0, 0);

    // 設定浮水印樣式
    const watermarkText = this.lastUsedStyle ? `渲染結果 - ${this.lastUsedStyle}` : '渲染結果';

    // 計算字體大小（根據圖片寬度動態調整）
    const fontSize = Math.max(24, Math.floor(canvas.width / 40));
    ctx.font = `bold ${fontSize}px "Microsoft YaHei", "PingFang TC", sans-serif`;

    // 測量文字寬度以設定背景
    const textMetrics = ctx.measureText(watermarkText);
    const textWidth = textMetrics.width;
    const textHeight = fontSize;
    const padding = fontSize / 2;

    // 計算浮水印位置（右下角）
    const x = canvas.width - textWidth - padding * 2 - 20;
    const y = canvas.height - textHeight - padding - 20;

    // 繪製半透明背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(x - padding, y - padding, textWidth + padding * 2, textHeight + padding * 2);

    // 繪製文字
    ctx.fillStyle = '#FFFFFF';
    ctx.textBaseline = 'top';
    ctx.fillText(watermarkText, x, y);

    // 下載圖片
    const filename = this.lastUsedStyle
      ? `styled_floorplan_${this.lastUsedStyle}_${Date.now()}.png`
      : `styled_floorplan_${Date.now()}.png`;

    canvas.toBlob((blob) => {
      if (blob) {
        downloadBlob(blob, filename);
      }
    }, 'image/png');
  }
}
